import { AlertColor, AlertTitle, Button, Input, Paper, Snackbar, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useTheme } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Divider, Typography } from "@mui/material";
import { Box } from "@mui/system";
import MultipleSelect from "../form_elements/MultipleSelect";
import HeaderFooter from "./HeaderFooter";
import { forwardRef, useCallback, useEffect, useState } from "react";
import QueryHelper from "../helpers/QueryHelper";
import FieldHelper from "../helpers/FieldHelper";
import { BASE, EVALUE, FIELD } from "../helpers/consts";
import { FieldSubLabel, FieldLabel } from "../form_elements/FieldLabel";
import { FieldContentDate, FieldContentText } from "../form_elements/FieldContent";
import FieldContainer from "../form_elements/FieldContainer";
import EnhancedTable from "./SelectTable";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import HomePage from "./HomePage";
import DisplayEvent from "./DisplayEvent";

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function GBMSearch() {
    const theme = useTheme();

    const [fieldHelper, setFieldHelper] = useState(new FieldHelper());
    const [queryHelper, setQueryHelper] = useState(new QueryHelper());

    const [searchResult, setSearchResult] = useState([]);
    const [countResult, setCountResult] = useState(0);
    const [timeMethod, setTimeMethod] = useState(false);

    const [singleEventOpen, setSingleEventOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<Event | undefined>(undefined);
    const [open, setOpen] = useState(false);
    const [snackData, setSnackData] = useState({ open: false, mode: "success" as AlertColor, message: "" });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    const method_names = ["Bayesian", "Poisson", "SNR"];
    const mode_names = ["Mode 1", "Mode 2", "Mode 3", "Mode 4"];

    const onMethodModeSelect = (id: string, value: string[]) => {
        if (id === "mode-select") {
            queryHelper.setMM(
                "modes",
                value.slice().map((mode) => mode.replace(" ", "_"))
            );
        } else if (id === "method-select") {
            queryHelper.setMM(
                "methods",
                value.slice().map((v) => (v === "SNR" ? "SN" : v))
            );
        }

        updateQueryHelper();
    };

    const onSingleEventClose = (event: any, reason: any) => {
        if (reason && reason === "backdropClick") {
            setSelectedEvent(undefined);
        }
    };

    const onSingleEventSelect = (event: any) => {
        setSelectedEvent(event);
    };

    const updateQueryHelper = () => {
        setQueryHelper(new QueryHelper(queryHelper));
    };

    const snack = (message: string, mode: AlertColor) => {
        setSnackData({ open: true, mode: mode, message: message });
    };

    const onTimeMethodChange = (event: any, selected: boolean) => {
        setTimeMethod(selected);

        if (selected) {
            onUpdate(false, FIELD.TRIGUTC, EVALUE.MIN, "");
            onUpdate(false, FIELD.TRIGUTC, EVALUE.MAX, "");
        } else {
            onUpdate(false, FIELD.TRIGTIME, EVALUE.MIN, "");
            onUpdate(false, FIELD.TRIGTIME, EVALUE.MAX, "");
        }
    };

    const onUpdate = (enabled: boolean, field: string, evalue: EVALUE, value: any) => {
        queryHelper.setQueryEnabled(field, evalue, enabled);
        queryHelper.setQueryValue(field, evalue, value);
        updateQueryHelper();
    };

    const onSubmit = (showResult = true) => {
        queryHelper.setMM("limit", rowsPerPage);
        queryHelper.setMM("offset", rowsPerPage * page);
        setOpen(true);
        fetch(`${BASE}/mysql_utils/post_query.php`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: "query=" + JSON.stringify(queryHelper.getQuery()),
        })
            .then((resp) => resp.json())
            .then((result) => {
                setSearchResult(result.slice(1));
                setOpen(false);
                showResult && snack(`${result[0].count} events found.`, "info");
                setCountResult(result[0].count);
            })
            .catch((reason) => {
                console.log(reason);
                snack(reason, "error");
            });

    };

    const handleCsvData = async () => {
        queryHelper.setMM("limit", 1000000);
        queryHelper.setMM("offset", 0);
        let data: any[] = [];

        snack("Downloading file. This might take a while. Users are advised not to download more than 50k events at once.", "info")
        setOpen(true)
        await fetch(`${BASE}/mysql_utils/post_query.php`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: "query=" + JSON.stringify(queryHelper.getQuery()),
        })
            .then((resp) => resp.json())
            .then((result) => {
                queryHelper.setMM("limit", rowsPerPage);
                queryHelper.setMM("offset", rowsPerPage * page);
                // Save result
                data = result.slice(1);
                setOpen(false);
                return result.slice(1);
                
            })
            .catch((reason) => {
                queryHelper.setMM("limit", rowsPerPage);
                queryHelper.setMM("offset", rowsPerPage * page);
                console.log(reason);
                snack(reason, "error");
                setOpen(false);
                return [];
            });

        // Undo
        queryHelper.setMM("limit", rowsPerPage);
        queryHelper.setMM("offset", rowsPerPage * page);
        setOpen(false);
        return data;
      };

    const updateRanges = (ranges?: any) => {
        let fieldHelper_ = new FieldHelper();
        fieldHelper_.field_data = fieldHelper.field_data;
        
        Object.entries(ranges).forEach(([field, values]) => {
            fieldHelper_.setMin(field, (values as any[])[0] as any);
            fieldHelper_.setMax(field, (values as any[])[1] as any);
        });

        setFieldHelper(fieldHelper_);
        setOpen(false);
        snack("Ranges Updated.", "success");
    };

    useEffect(() => {
        setOpen(true);

        fetch(`${BASE}/mysql_utils/get_ranges.php`, {
            method: "GET",
        })
            .then((resp) => resp.json())
            .then((data) => updateRanges(data))
            .catch((reason) => {
                console.log(reason);
                snack(reason, "error");
            });
    }, []);

    useEffect(() => {
        onSubmit(false);
    }, [page, rowsPerPage]);

    return (
        <>
            <HeaderFooter>
                <Typography style={theme.h1}>GBM Database Search</Typography>
                <Divider style={theme.divider} />
                <Box m={{ md: 4 }} mt={4}>
                    <Stack direction={"column"} spacing={2} justifyContent="stretch" mb={2}>
                        <Stack direction="row" spacing={2} justifyContent={"center"}>
                            <MultipleSelect id="method-select" label={"Methods"} contents={method_names} onChange={onMethodModeSelect}></MultipleSelect>
                            <MultipleSelect id="mode-select" label={"Modes"} contents={mode_names} onChange={onMethodModeSelect}></MultipleSelect>
                            <Button variant="contained" onClick={() => onSubmit()} sx={{ minWidth: "100px" }}>
                                Search
                            </Button>
                        </Stack>

                        <Stack spacing={1}>
                            <FieldContainer label="Time">
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <FieldSubLabel>{fieldHelper.getLabel(FIELD.TRIGUTC)}</FieldSubLabel>
                                    <Switch onChange={onTimeMethodChange} />
                                    <FieldSubLabel>{fieldHelper.getLabel(FIELD.TRIGTIME)}</FieldSubLabel>
                                </Stack>

                                {timeMethod ? (
                                    <FieldContentText field={FIELD.TRIGTIME} min={fieldHelper.getMin(FIELD.TRIGTIME)} max={fieldHelper.getMax(FIELD.TRIGTIME)} onUpdate={onUpdate}></FieldContentText>
                                ) : (
                                    <FieldContentDate field={FIELD.TRIGUTC} min={fieldHelper.getMin(FIELD.TRIGUTC)} max={fieldHelper.getMax(FIELD.TRIGUTC)} onUpdate={onUpdate}></FieldContentDate>
                                )}
                            </FieldContainer>

                            <FieldContainer label={fieldHelper.getLabel(FIELD.DURATION)} dec_short='(Second)'>
                                <FieldContentText field={FIELD.DURATION} min={fieldHelper.getMin(FIELD.DURATION)} max={fieldHelper.getMax(FIELD.DURATION)} onUpdate={onUpdate}></FieldContentText>
                            </FieldContainer>

                            <FieldContainer label="Hardness Ratio" dec_short="(HR)" dec_long="HR is defined as (Epiv--2000 keV) / (10 keV--Epiv), where Epiv = 25, 50, 75, or 300 keV">
                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.HR_25)}</FieldSubLabel>
                                <FieldContentText field={FIELD.HR_25} min={fieldHelper.getMin(FIELD.HR_25)} max={fieldHelper.getMax(FIELD.HR_25)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.HR_50)}</FieldSubLabel>
                                <FieldContentText field={FIELD.HR_50} min={fieldHelper.getMin(FIELD.HR_50)} max={fieldHelper.getMax(FIELD.HR_50)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.HR_75)}</FieldSubLabel>
                                <FieldContentText field={FIELD.HR_75} min={fieldHelper.getMin(FIELD.HR_75)} max={fieldHelper.getMax(FIELD.HR_75)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.HR_300)}</FieldSubLabel>
                                <FieldContentText field={FIELD.HR_300} min={fieldHelper.getMin(FIELD.HR_300)} max={fieldHelper.getMax(FIELD.HR_300)} onUpdate={onUpdate}></FieldContentText>
                            </FieldContainer>
                           
                            <FieldContainer label="Event Class" dec_short="(Bayesian Probability)">

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.SGR)}</FieldSubLabel>
                                <FieldContentText field={FIELD.SGR} min={fieldHelper.getMin(FIELD.SGR)} max={fieldHelper.getMax(FIELD.SGR)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.SGRB)}</FieldSubLabel>
                                <FieldContentText field={FIELD.SGRB} min={fieldHelper.getMin(FIELD.SGRB)} max={fieldHelper.getMax(FIELD.SGRB)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.LGRB)}</FieldSubLabel>
                                <FieldContentText field={FIELD.LGRB} min={fieldHelper.getMin(FIELD.LGRB)} max={fieldHelper.getMax(FIELD.LGRB)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.SFLARE)}</FieldSubLabel>
                                <FieldContentText field={FIELD.SFLARE} min={fieldHelper.getMin(FIELD.SFLARE)} max={fieldHelper.getMax(FIELD.SFLARE)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.TGF)}</FieldSubLabel>
                                <FieldContentText field={FIELD.TGF} min={fieldHelper.getMin(FIELD.TGF)} max={fieldHelper.getMax(FIELD.TGF)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.TRANSNT)}</FieldSubLabel>
                                <FieldContentText field={FIELD.TRANSNT} min={fieldHelper.getMin(FIELD.TRANSNT)} max={fieldHelper.getMax(FIELD.TRANSNT)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.DISTPAR)}</FieldSubLabel>
                                <FieldContentText field={FIELD.DISTPAR} min={fieldHelper.getMin(FIELD.DISTPAR)} max={fieldHelper.getMax(FIELD.DISTPAR)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.LOCLPAR)}</FieldSubLabel>
                                <FieldContentText field={FIELD.LOCLPAR} min={fieldHelper.getMin(FIELD.LOCLPAR)} max={fieldHelper.getMax(FIELD.LOCLPAR)} onUpdate={onUpdate}></FieldContentText>

                                <FieldSubLabel>{fieldHelper.getLabel(FIELD.NO_EVALUATION)}</FieldSubLabel>
                                <FieldContentText field={FIELD.NO_EVALUATION} min={fieldHelper.getMin(FIELD.NO_EVALUATION)} max={fieldHelper.getMax(FIELD.NO_EVALUATION)} onUpdate={onUpdate}></FieldContentText>
                            </FieldContainer>
                        </Stack>

                        <Stack direction={"row"} justifyContent="right"></Stack>
                    </Stack>

                    {searchResult.length > 0 ? <EnhancedTable onSelect={onSingleEventSelect} rows={searchResult} setPage={setPage} setRowsPerPage={setRowsPerPage} page={page} rowsPerPage={rowsPerPage} count={countResult} onDownloadCsv={handleCsvData}></EnhancedTable> : ""}
                </Box>

                <Backdrop
                    sx={{
                        color: "#fff",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}
                    open={open}
                    onClick={undefined}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <Snackbar sx={{ margin: "0px 0px 30px 0px" }} anchorOrigin={{ horizontal: "center", vertical: "bottom" }} open={snackData.open} autoHideDuration={3000} onClose={() => setSnackData({ ...snackData, open: false })}>
                    <Alert severity={snackData.mode} sx={{ fontSize: "large", width: "100%" }}>
                        {snackData.message}
                    </Alert>
                </Snackbar>
            </HeaderFooter>
            <DisplayEvent event={selectedEvent} 
            onClose={onSingleEventClose}></DisplayEvent>
        </>
    );
}
